import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'

// ─── Design tokens (dark academic theme) ─────────────────────────────────────
const M = '#06060c'
const D = '#0a0a16'
const G = '#d4a843'
const EM = '#10b981'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.04)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'

// ─── The 4 free foundation subjects ──────────────────────────────────────────
// category_id + class_id are confirmed in the Sage Supabase DB.
// Sessions run through /learn/session?category=<cid>&class=<clid>
// using EducationSessionPage → useEducationSession → ai-education edge fn.
// prompt-builder.ts in ai-education handles age-adaptive teaching automatically.

interface PrepModule { title: string; lessons: string[] }
interface PrepSubject {
    slug: string
    category_id: string
    class_id: string
    icon: string
    label: string
    tagline: string
    overview: string
    modules: PrepModule[]
}

const DEFAULT_PREP_DATA: Record<string, PrepModule[]> = {
    'prep-critical-thinking': [
        {
            title: 'What Is Critical Thinking? Thinking About Thinking',
            lessons: [
                'What Is Critical Thinking?, The Art of Thinking Clearly',
                'Why We Need Critical Thinking, Avoiding Foolishness',
                'Facts vs. Opinions, Knowing the Difference',
            ],
        },
        {
            title: 'Arguments, How Reasoning Works',
            lessons: [
                'What Is an Argument? Premises and Conclusions',
                'Deductive vs. Inductive Arguments',
                'Evaluating Arguments, Are They Good or Bad?',
            ],
        },
        {
            title: 'Logical Fallacies, Errors in Reasoning',
            lessons: [
                'What Are Fallacies? Common Mistakes in Arguments',
                'Ad Hominem, Attacking the Person, Not the Argument',
                'Straw Man, Misrepresenting an Argument',
            ],
        },
        {
            title: 'Cognitive Biases and Questioning Evidence',
            lessons: [
                'Confirmation Bias, Seeing Only What You Want to See',
                'Evaluating Sources and Evidence',
                'Statistics, How Numbers Can Lie',
                'Correlation vs. Causation, A Common Mistake',
            ],
        },
        {
            title: 'Building Your Own Arguments',
            lessons: [
                'Constructing a Good Argument',
                'Supporting Your Claims, Using Evidence',
                'Anticipating Counterarguments, Steelmanning',
            ],
        },
        {
            title: 'Critical Thinking in Everyday Life',
            lessons: [
                'Critical Thinking and Social Media',
                'Critical Thinking and Advertising',
                'Critical Thinking in School, Studying and Learning',
                'Thinking for Yourself, Independence and Humility',
            ],
        },
    ],
    'prep-study-skills': [
        {
            title: 'How Memory Works, The Science of Learning',
            lessons: [
                'Encoding, Storage, and Retrieval, The Three Stages of Memory',
                'The Forgetting Curve, Why We Forget and How to Beat It',
                'Working Memory and Cognitive Load, Don\'t Overload Your Brain',
                'Sleep and Memory Consolidation, Why All-Nighters Don\'t Work',
                'Types of Memory, Episodic, Semantic, and Procedural',
            ],
        },
        {
            title: 'Active Learning Strategies, Study Smarter, Not Harder',
            lessons: [
                'Active Recall, The Most Powerful Study Technique',
                'Spaced Repetition, Timing Your Reviews for Maximum Retention',
                'Interleaving, Mixing It Up for Deeper Learning',
                'Elaboration and Self-Explanation, Making Connections',
                'Concrete Examples, Making Abstract Ideas Real',
            ],
        },
        {
            title: 'Reading and Note-Taking, Capturing Ideas Effectively',
            lessons: [
                'SQ3R, A System for Active Reading',
                'How to Read a Textbook, Strategies for Different Subjects',
                'The Cornell Note-Taking System',
                'Mind Mapping, Visualizing Connections',
                'Highlighting, When It Works and When It Doesn\'t',
            ],
        },
        {
            title: 'Time Management and Focus, Making Time to Learn',
            lessons: [
                'The Pomodoro Technique, Work in Short Bursts',
                'Managing Distractions, Creating a Focus-Friendly Environment',
                'Setting Goals, SMART Goals and Priorities',
                'Planning Your Study Sessions, The Weekly Review',
                'Dealing with Procrastination, Understanding and Overcoming It',
            ],
        },
        {
            title: 'Test-Taking and Exam Strategies',
            lessons: [
                'Preparing for Different Types of Exams',
                'Strategies for Multiple-Choice Tests',
                'Strategies for Essay Exams',
                'Managing Test Anxiety, Staying Calm Under Pressure',
                'Learning from Mistakes, Reviewing Your Errors',
            ],
        },
        {
            title: 'Putting It All Together, Your Personal Study System',
            lessons: [
                'Creating Your Personal Study System',
                'Using Technology Wisely, Apps and Tools for Learning',
                'Healthy Habits for Learning, Sleep, Nutrition, Exercise',
                'Lifelong Learning, Skills for the Future',
                'Review and Reflection, Your Learning Journey',
            ],
        },
    ],
    'prep-career-exploration': [
        {
            title: 'Understanding Yourself, Who You Are and What You Value',
            lessons: [
                'What Is Career Exploration? Why Start with You?',
                'Interests, What Do You Love to Do?',
                'Strengths, What Are You Good At?',
                'Values, What Matters Most to You?',
                'Personality, How You Approach the World',
                'Putting It Together, Your Personal Profile',
            ],
        },
        {
            title: 'Exploring Careers, What\'s Out There?',
            lessons: [
                'The World of Work, An Overview of Career Fields',
                'Researching Careers, How to Find Out What a Job Is Really Like',
                'Informational Interviews, Talking to Real People',
                'Job Market Trends, Growing and Declining Fields',
                'Education and Training Pathways, How to Get There',
            ],
        },
        {
            title: 'Making Connections, Networking and Experience',
            lessons: [
                'What Is Networking? Building Relationships',
                'LinkedIn and Online Presence, Your Digital Professional Self',
                'Gaining Experience, Volunteering, Internships, and Part‑Time Jobs',
                'Resumes and Cover Letters, Telling Your Story',
            ],
        },
        {
            title: 'Making Decisions and Planning Ahead',
            lessons: [
                'Decision‑Making Frameworks, How to Choose',
                'Overcoming Analysis Paralysis, Taking Action',
                'Creating a Personal Career Plan',
                'Financial Considerations, Understanding Salaries and Costs',
                'Changing Your Mind, It\'s Okay to Pivot',
            ],
        },
        {
            title: 'Putting It All Together, Your Future Starts Now',
            lessons: [
                'Exploring High School Options, Courses and Activities',
                'College and Career Readiness, What It Means',
                'Goal Setting, Your Next Steps',
                'Course Review, Your Career Exploration Journey',
            ],
        },
    ],
    'prep-personal-finance': [
        {
            title: 'Money Foundations, What Is Money and How Does It Work?',
            lessons: [
                'What Is Money? A History of Exchange',
                'The Value of Money, Why Prices Change (Inflation)',
                'Interest, The Price of Borrowing and Reward for Saving',
                'Needs vs. Wants, The Foundation of Smart Spending',
                'The Time Value of Money, Why a Dollar Today Is Worth More Tomorrow',
            ],
        },
        {
            title: 'Earning Money, Income and Career Choices',
            lessons: [
                'Ways to Earn, Jobs, Self-Employment, and Passive Income',
                'Gross Pay vs. Net Pay, Understanding Your Paycheck',
                'Income Taxes, How They Work and Why We Pay Them',
                'Paying Yourself First, The Habit of Saving',
                'Your Earning Potential, Education, Skills, and Career Choices',
            ],
        },
        {
            title: 'Banking and Financial Institutions',
            lessons: [
                'What Is a Bank? How Banks Work',
                'Types of Bank Accounts, Checking, Savings, and More',
                'Online Banking, Apps, and Digital Payments',
                'Debit Cards vs. Credit Cards, What\'s the Difference?',
                'Credit Unions vs. Banks, What\'s the Difference?',
            ],
        },
        {
            title: 'Budgeting and Spending Wisely',
            lessons: [
                'What Is a Budget? Planning Your Spending',
                'Tracking Your Spending, Where Does It All Go?',
                'Smart Shopping, Comparing Prices and Value',
                'Advertising and Marketing, How They Influence Your Choices',
                'Consumer Rights and Responsibilities',
            ],
        },
        {
            title: 'Credit and Debt, Borrowing Money',
            lessons: [
                'What Is Credit? Borrowing and Lending',
                'Interest Rates and APR, The Cost of Borrowing',
                'Credit Scores, What They Are and Why They Matter',
                'Good Debt vs. Bad Debt',
                'Avoiding Debt Traps, Payday Loans, Overdrafts, and Scams',
            ],
        },
        {
            title: 'Saving and Investing Basics',
            lessons: [
                'The Power of Saving, Why and How to Save',
                'Compound Interest, How Money Grows Over Time',
                'Saving vs. Investing, What\'s the Difference?',
                'Introduction to Investing, Stocks, Bonds, and ETFs',
                'Building Your Financial Future, A Personal Action Plan',
            ],
        },
    ],
}

function getSubjects(t: ReturnType<typeof useTranslation>['t'], dynamicData?: Record<string, PrepModule[]>): PrepSubject[] {
    const b = 'prep.programmes'
    const data = dynamicData || DEFAULT_PREP_DATA
    return [
        {
            slug: 'prep-critical-thinking',
            category_id: 'd8eb9872-59a4-41db-9494-4a542c9fd461',
            class_id: '72b58fdd-0847-4263-8984-77cb72b1fc95',
            icon: '🔍',
            label: t(`${b}.prep-critical-thinking.label`, 'Critical Thinking'),
            tagline: t(`${b}.prep-critical-thinking.tagline`, 'The foundation of all university study'),
            overview: t(`${b}.prep-critical-thinking.overview`, 'Learn to evaluate evidence, construct arguments and spot logical fallacies. Sage will teach you how to think, not what to think.'),
            modules: data['prep-critical-thinking'] || DEFAULT_PREP_DATA['prep-critical-thinking']
        },
        {
            slug: 'prep-study-skills',
            category_id: 'd4ef9f9f-f991-4ba9-bbc9-7cf17c1b2f10',
            class_id: 'c2d365e1-cd5d-4417-9986-182dcc089618',
            icon: '📚',
            label: t(`${b}.prep-study-skills.label`, 'Study Skills & Learning Science'),
            tagline: t(`${b}.prep-study-skills.tagline`, 'Learn how to learn, before you start learning'),
            overview: t(`${b}.prep-study-skills.overview`, 'Most people study wrong. This programme teaches spaced repetition, active recall and how memory actually works so everything you study sticks.'),
            modules: data['prep-study-skills'] || DEFAULT_PREP_DATA['prep-study-skills']
        },
        {
            slug: 'prep-career-exploration',
            category_id: 'f6914f53-f7e9-4222-9b9a-c91110d215e8',
            class_id: '7bb810fa-0c92-424a-91c0-7a6a048effad',
            icon: '🎯',
            label: t(`${b}.prep-career-exploration.label`, 'Career Exploration'),
            tagline: t(`${b}.prep-career-exploration.tagline`, 'Figure out where you\'re going before you start'),
            overview: t(`${b}.prep-career-exploration.overview`, 'Understand what careers really look like, research pathways honestly and make study decisions you won\'t regret, before committing.'),
            modules: data['prep-career-exploration'] || DEFAULT_PREP_DATA['prep-career-exploration']
        },
        {
            slug: 'prep-personal-finance',
            category_id: 'f6d4b1f0-2cff-43b3-8302-b19c668d7aa1',
            class_id: '82e428c4-f80c-4474-a930-5e3eff6ccb59',
            icon: '💳',
            label: t(`${b}.prep-personal-finance.label`, 'Personal Finance Foundations'),
            tagline: t(`${b}.prep-personal-finance.tagline`, 'The financial literacy every adult needs'),
            overview: t(`${b}.prep-personal-finance.overview`, 'Budgeting, debt, saving and investing basics, the financial fundamentals most people were never taught, delivered by your Sage AI tutor.'),
            modules: data['prep-personal-finance'] || DEFAULT_PREP_DATA['prep-personal-finance']
        },
    ]
}

// ─── Programme Card ───────────────────────────────────────────────────────────
function SubjectCard({
    subject, expanded, onToggle, isMobile,
}: {
    subject: PrepSubject; expanded: boolean; onToggle: () => void; isMobile: boolean
}) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { user, userProfile } = useAuth()
    const [loading, setLoading] = useState(false)

    async function handleStart() {
        if (loading) return
        if (!user) {
            // Save prep intent and redirect to sign up
            localStorage.setItem('sage_prep_intent', 'true')
            const dest = `/learn/session?category=${subject.category_id}&class=${subject.class_id}`
            navigate(`/education-signup?hub=solo&redirect=${encodeURIComponent(dest)}`)
            return
        }
        setLoading(true)
        navigate(`/learn/session?category=${subject.category_id}&class=${subject.class_id}`)
        setLoading(false)
    }

    // Try to show an age label from the profile
    const ageLabel = (userProfile as any)?.child_age
        ? `age ${(userProfile as any).child_age}`
        : (userProfile as any)?.age_bracket || 'your age group'

    return (
        <motion.div layout style={{
            background: expanded ? 'rgba(16,185,129,0.05)' : CA,
            border: `${expanded ? 2 : 1}px solid ${expanded ? EM : BR}`,
            borderRadius: 20, overflow: 'hidden',
            transition: 'border-color 0.2s, background 0.2s',
        }}>
            {/* Header row */}
            <div onClick={onToggle} style={{
                padding: isMobile ? '18px 16px' : '22px 24px',
                cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 16
            }}>
                <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: 'rgba(16,185,129,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22, flexShrink: 0
                }}>{subject.icon}</div>

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontWeight: 800, fontSize: isMobile ? 14 : 16, color: TX }}>{subject.label}</span>
                        <span style={{
                            fontSize: 10, fontWeight: 800, letterSpacing: '1px',
                            background: 'rgba(16,185,129,0.15)', color: EM,
                            borderRadius: 100, padding: '2px 8px'
                        }}>{t('prep.card.freeForever', 'FREE FOREVER')}</span>
                    </div>
                    <p style={{ fontSize: 13, color: MU, margin: '0 0 6px', lineHeight: 1.4 }}>{subject.tagline}</p>
                    <span style={{ fontSize: 11, color: MU }}>
                        {subject.modules.length} {t('prep.card.modules', 'modules')} · {subject.modules.reduce((s, m) => s + m.lessons.length, 0)} {t('prep.card.lessons', 'lessons')}
                    </span>
                </div>

                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 900, color: EM, marginBottom: 2 }}>{t('prep.card.free', 'FREE')}</div>
                    <div style={{ fontSize: 12, color: MU }}>{expanded ? '▲' : '▼'}</div>
                </div>
            </div>

            {/* Expanded body */}
            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div style={{ padding: isMobile ? '0 16px 20px' : '0 24px 24px', borderTop: `1px solid ${BR}` }}>
                            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, margin: '20px 0' }}>
                                {subject.overview}
                            </p>

                            {/* Module grid */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                                gap: 12, marginBottom: 20
                            }}>
                                {subject.modules.map((mod, mi) => (
                                    <div key={mi} style={{
                                        background: 'rgba(255,255,255,0.03)',
                                        border: `1px solid ${BR}`,
                                        borderRadius: 12, padding: '14px 16px'
                                    }}>
                                        <p style={{
                                            fontSize: 11, fontWeight: 800, color: EM,
                                            margin: '0 0 10px', letterSpacing: '0.5px'
                                        }}>
                                            {t('prep.card.modulePrefix', 'MODULE')} {mi + 1} · {mod.title.replace(/^Module \d+:\s*/i, '').toUpperCase()}
                                        </p>
                                        {mod.lessons.map((lesson, li) => (
                                            <div key={li} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                                                <span style={{ color: EM, fontSize: 10, fontWeight: 900, flexShrink: 0, marginTop: 3 }}>›</span>
                                                <span style={{ fontSize: 12, color: MU, lineHeight: 1.45 }}>{lesson}</span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>

                            {/* Age-adaptive notice */}
                            <div style={{
                                background: 'rgba(14,165,233,0.05)',
                                border: '1px solid rgba(14,165,233,0.15)',
                                borderRadius: 12, padding: '12px 16px', marginBottom: 20,
                            }}>
                                <p style={{ fontSize: 12, color: MU, margin: 0, lineHeight: 1.6 }}>
                                    {t('prep.ageNotice', {
                                        age: ageLabel,
                                        defaultValue: `Sage dynamically adapts vocabulary, examples, and pacing for ${ageLabel}. You can change this anytime in profile settings.`,
                                    })}
                                </p>
                            </div>

                            {/* CTA */}
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.97 }}
                                onClick={handleStart}
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    background: `linear-gradient(135deg, ${EM}, #059669)`,
                                    color: '#fff', border: 'none', borderRadius: 12, padding: '14px',
                                    fontSize: 14, fontWeight: 800, cursor: 'pointer',
                                    boxShadow: '0 4px 20px rgba(16,185,129,0.2)',
                                }}
                            >
                                {loading ? t('prep.card.loading', 'Loading…') : user ? t('prep.card.startFree', { label: subject.label }) : t('prep.card.signInStart', 'Sign In & Start Free →')}
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function PrepPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { user } = useAuth()
    const [liveData, setLiveData] = useState<Record<string, PrepModule[]>>(DEFAULT_PREP_DATA)

    useEffect(() => {
        Promise.resolve(
            supabase.from('classes')
                .select('id, module_plan')
                .in('id', [
                    '72b58fdd-0847-4263-8984-77cb72b1fc95',
                    'c2d365e1-cd5d-4417-9986-182dcc089618',
                    '7bb810fa-0c92-424a-91c0-7a6a048effad',
                    '82e428c4-f80c-4474-a930-5e3eff6ccb59'
                ])
        ).then(({ data }) => {
            if (data && data.length > 0) {
                const mapped: Record<string, PrepModule[]> = {}
                const idToSlug: Record<string, string> = {
                    '72b58fdd-0847-4263-8984-77cb72b1fc95': 'prep-critical-thinking',
                    'c2d365e1-cd5d-4417-9986-182dcc089618': 'prep-study-skills',
                    '7bb810fa-0c92-424a-91c0-7a6a048effad': 'prep-career-exploration',
                    '82e428c4-f80c-4474-a930-5e3eff6ccb59': 'prep-personal-finance',
                }
                data.forEach((c: any) => {
                    const slug = idToSlug[c.id]
                    if (slug && Array.isArray(c.module_plan) && c.module_plan.length > 0) {
                        mapped[slug] = c.module_plan.map((m: any) => ({
                            title: (m.title || '').replace(/^Module \d+:\s*/i, ''),
                            lessons: (m.lessons || []).map((l: any) => l.title || l).filter(Boolean)
                        }))
                    }
                })
                setLiveData(prev => ({ ...prev, ...mapped }))
            }
        }).catch(() => {})
    }, [])

    const subjects = getSubjects(t, liveData)
    const [expanded, setExpanded] = useState<string | null>('prep-critical-thinking')
    const [isMobile, setIsMobile] = useState(false)

    const totalModules = subjects.reduce((s, sub) => s + sub.modules.length, 0)
    const totalLessons = subjects.reduce((s, sub) => s + sub.modules.reduce((sm, m) => sm + m.lessons.length, 0), 0)

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 1024)
        check()
        window.addEventListener('resize', check)
        return () => window.removeEventListener('resize', check)
    }, [])

    return (
        <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
            <title>{t('prep.pageTitle', 'Free Foundation Subjects · Sage AI')}</title>

            {/* ── Hero ── */}
            <div style={{
                background: `linear-gradient(170deg, #07071a 0%, ${D} 60%, ${M} 100%)`,
                borderBottom: `1px solid ${BR}`, position: 'relative', overflow: 'hidden'
            }}>
                {/* Dot grid */}
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'radial-gradient(circle, rgba(16,185,129,0.06) 1px, transparent 1px)',
                    backgroundSize: '32px 32px', pointerEvents: 'none'
                }} />

                <div style={{
                    maxWidth: 800, margin: '0 auto',
                    padding: isMobile ? '44px 20px 40px' : '64px 24px 56px',
                    textAlign: 'center', position: 'relative'
                }}>
                    <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}>
                        {/* Eyebrow badge */}
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                            background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)',
                            borderRadius: 100, padding: '6px 16px', marginBottom: 20
                        }}>
                            <span style={{ fontSize: 14 }}>🎓</span>
                            <span style={{ fontSize: 11, fontWeight: 800, color: EM, letterSpacing: '2px' }}>
                                {t('prep.eyebrow', 'PREP · START HERE')}
                            </span>
                        </div>

                        <h1 style={{
                            fontSize: isMobile ? 28 : 44, fontWeight: 900,
                            margin: '0 0 16px', letterSpacing: '-1px',
                            fontFamily: "'Playfair Display', Georgia, serif", lineHeight: 1.1,
                            color: '#fff'
                        }}>
                            {t('prep.title', 'Start Here. Foundations Free.')}
                        </h1>

                        <p style={{ color: MU, fontSize: isMobile ? 14 : 17, lineHeight: 1.75, maxWidth: 580, margin: '0 auto 28px' }}>
                            {t('prep.sub', 'Four foundation subjects are completely free. Sign in to start learning and save your progress. Your Sage AI tutor adapts to your age and pace automatically.')}
                        </p>

                        {/* Stats strip */}
                        <div style={{
                            display: 'flex', gap: isMobile ? 16 : 32,
                            background: CA, border: `1px solid ${BR}`,
                            borderRadius: isMobile ? 24 : 100, padding: isMobile ? '16px' : '12px 28px',
                            flexWrap: 'wrap', justifyContent: 'center', width: 'fit-content', margin: '0 auto'
                        }}>
                            {[
                                { value: '4', label: t('prep.stats.foundations', 'Foundation subjects') },
                                { value: String(totalModules), label: t('prep.stats.modules', 'Total modules') },
                                { value: String(totalLessons), label: t('prep.stats.lessons', 'Free lessons') },
                                { value: '$0', label: t('prep.stats.cost', 'to start') },
                            ].map(s => (
                                <div key={s.label} style={{ textAlign: 'center', padding: '0 8px' }}>
                                    <div style={{ fontSize: 20, fontWeight: 900, color: EM, lineHeight: 1 }}>{s.value}</div>
                                    <div style={{ fontSize: 10, color: MU, fontWeight: 600, marginTop: 2 }}>{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>

            <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '32px 16px 80px' : '52px 24px 100px' }}>

                {/* ── Commitment banner ── */}
                <div style={{
                    background: 'rgba(212,168,67,0.04)', border: '1px solid rgba(212,168,67,0.15)',
                    borderRadius: 16, padding: '18px 24px', marginBottom: 32,
                    display: 'flex', gap: 14, alignItems: 'flex-start'
                }}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>⚖️</span>
                    <div>
                        <p style={{ fontSize: 13, fontWeight: 700, color: G, margin: '0 0 4px' }}>
                            {t('prep.commitmentTitle', 'Our Commitment to Truth Over Bias')}
                        </p>
                        <p style={{ fontSize: 13, color: MU, margin: 0, lineHeight: 1.6 }}>
                            {t('prep.commitmentText', 'Still Waters Academy presents facts as established through the scientific method and labels theories as theories. We do not lean left or right, we equip you to think critically, not tell you what to think.')}
                        </p>
                    </div>
                </div>

                {/* ── Age-aware AI notice ── */}
                <div style={{
                    background: 'rgba(14,165,233,0.04)', border: '1px solid rgba(14,165,233,0.12)',
                    borderRadius: 16, padding: '16px 20px', marginBottom: 40,
                    display: 'flex', gap: 12, alignItems: 'flex-start'
                }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>🧠</span>
                    <div>
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#7dd3fc', margin: '0 0 4px' }}>
                            {t('prep.ageAdaptiveTitle', 'Sage Adapts to Every Age')}
                        </p>
                        <p style={{ fontSize: 12, color: MU, margin: 0, lineHeight: 1.6 }}>
                            {t('prep.ageAdaptiveText', "Whether you're studying yourself or setting this up for a child, Sage (Our AI Tutor) automatically adjusts vocabulary, examples and pacing based on the learner's age set in their profile. A 10-year-old and a 30-year-old get the same essential concepts, delivered very differently.")}
                        </p>
                    </div>
                </div>

                {/* ── Section header ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                    <div style={{ height: 1, flex: 1, background: 'linear-gradient(90deg, rgba(16,185,129,0.4), transparent)' }} />
                    <span style={{ fontSize: 11, fontWeight: 800, color: EM, letterSpacing: '2px', whiteSpace: 'nowrap' }}>
                        {t('prep.foundations.title', 'FOUNDATION SUBJECTS · ALWAYS FREE')}
                    </span>
                    <div style={{ height: 1, flex: 1, background: 'linear-gradient(90deg, transparent, rgba(16,185,129,0.4))' }} />
                </div>

                <p style={{ fontSize: 13, color: MU, textAlign: 'center', margin: '0 0 28px', lineHeight: 1.6 }}>
                    {t('prep.foundations.sub', 'These four subjects are permanently free. Sign in to save your progress automatically.')}
                </p>

                {/* ── Subject cards ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 52 }}>
                    {subjects.map(subject => (
                        <SubjectCard
                            key={subject.slug}
                            subject={subject}
                            expanded={expanded === subject.slug}
                            onToggle={() => setExpanded(expanded === subject.slug ? null : subject.slug)}
                            isMobile={isMobile}
                        />
                    ))}
                </div>

                {/* ── How it works ── */}
                <div style={{
                    background: 'rgba(14,165,233,0.04)', border: '1px solid rgba(14,165,233,0.15)',
                    borderRadius: 20, padding: isMobile ? '24px 20px' : '32px 36px', marginBottom: 52
                }}>
                    <p style={{ fontSize: 11, fontWeight: 800, color: '#7dd3fc', letterSpacing: '2px', marginBottom: 20, textAlign: 'center' }}>
                        {t('prep.prereqs.title', 'HOW IT WORKS')}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 20 }}>
                        {[
                            {
                                icon: '🟢',
                                title: t('prep.prereqs.step1Title', 'Foundations Free'),
                                desc: t('prep.prereqs.step1Desc', 'Critical Thinking, Study Skills, Career Exploration, Personal Finance:  sign in to track progress for free.')
                            },
                            {
                                icon: '🧠',
                                title: 'Age-Adaptive AI Tutor',
                                desc: 'Sage automatically adjusts teaching style, vocabulary and examples based on the learner\'s age, set once in their profile.'
                            },
                            {
                                icon: '🎓',
                                title: t('prep.prereqs.step3Title', 'Upgrade for More'),
                                desc: t('prep.prereqs.step3Desc', 'Unlock the full homeschool curriculum, Elite Academy or StillWaters University with a subscription.')
                            },
                        ].map(item => (
                            <div key={item.title} style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 28, marginBottom: 12 }}>{item.icon}</div>
                                <p style={{ fontWeight: 700, fontSize: 14, margin: '0 0 8px', color: TX }}>{item.title}</p>
                                <p style={{ fontSize: 13, color: MU, lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Bottom CTAs ── */}
                <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 15, color: MU, marginBottom: 20 }}>
                        {t('prep.ctaTitle', 'Ready to explore the full Sage curriculum?')}
                    </p>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {!user && (
                            <motion.button
                                whileHover={{ scale: 1.03 }}
                                onClick={() => navigate('/education-signup?hub=solo&redirect=' + encodeURIComponent('/learn/session?category=d8eb9872-59a4-41db-9494-4a542c9fd461&class=72b58fdd-0847-4263-8984-77cb72b1fc95'))}
                                style={{
                                    background: `linear-gradient(135deg, ${EM}, #059669)`,
                                    color: '#fff', border: 'none', borderRadius: 12,
                                    padding: '14px 32px', fontSize: 14, fontWeight: 800, cursor: 'pointer'
                                }}
                            >
                                {t('prep.cta.createAccount', 'Create Free Account →')}
                            </motion.button>
                        )}

                        <motion.button
                            whileHover={{ scale: 1.03 }}
                            onClick={() => navigate('/homeschool')}
                            style={{
                                background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.3)',
                                color: '#7dd3fc', borderRadius: 12,
                                padding: '14px 32px', fontSize: 14, fontWeight: 700, cursor: 'pointer'
                            }}
                        >
                            {t('prep.cta.homeschool', 'Homeschool Programme →')}
                        </motion.button>
                    </div>
                </div>
            </div>
        </div>
    )
}
